import React, { useCallback, useReducer } from 'react';
import {
	CreateModifierContext,
	createModifierInitialState,
	TCreateModifierVisibleForm,
	TTypeOfCreateModifierModalWindow,
} from './createModifierContext';
import { createModifierReducer } from './createModifierReducer';
import { CreateModifierActionTypes } from './createModifierActions';
import {
	TFileId,
	ICreateModifierValidationError,
	IItemForGetShortInfo,
	IItemForGetShortInfoWithStatus,
	IServerError,
	TLanguageIDs,
	IDefaultLanguage,
	ICreateModifier,
	ITotalPrice,
} from 'types';

const CreateModifierState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(createModifierReducer, createModifierInitialState);

	const changeVisibleForm = useCallback((value: TCreateModifierVisibleForm) => {
		dispatch({
			type: CreateModifierActionTypes.CHANGE_VISIBLE_FORM,
			payload: {
				visibleForm: value,
			},
		});
	}, []);

	const setFileId = useCallback((value: TFileId) => {
		dispatch({
			type: CreateModifierActionTypes.SET_FILE_ID,
			payload: {
				fileId: value,
			},
		});
	}, []);

	const setFetchedCustomers = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateModifierActionTypes.SET_FETCHED_CUSTOMERS,
			payload: {
				customers: value,
			},
		});
	}, []);

	const setCustomer = useCallback((value: Nullable<string>) => {
		dispatch({
			type: CreateModifierActionTypes.SET_CUSTOMER,
			payload: {
				customer: value,
			},
		});
	}, []);

	const setFetchedRestaurants = useCallback((value: IItemForGetShortInfoWithStatus[]) => {
		dispatch({
			type: CreateModifierActionTypes.SET_FETCHED_RESTAURANTS,
			payload: {
				restaurants: value,
			},
		});
	}, []);

	const setRestaurantsForPrice = useCallback((value: string[]) => {
		dispatch({
			type: CreateModifierActionTypes.SET_RESTAURANTS_FOR_PRICE,
			payload: {
				restaurants: value,
			},
		});
	}, []);

	const changePrice = useCallback((value: string) => {
		dispatch({
			type: CreateModifierActionTypes.CHANGE_PRICE,
			payload: {
				price: value,
			},
		});
	}, []);

	const addPrices = useCallback((value: ITotalPrice[]) => {
		dispatch({
			type: CreateModifierActionTypes.ADD_PRICE,
			payload: {
				prices: value,
			},
		});
	}, []);

	const removePrice = useCallback(
		(value: string[]) => {
			const updatedTotalPrices = state.totalPrices.filter((el) => !value.includes(el.restaurantId));
			const updatedRestaurantsForPrices = state.restaurantsForPrice.filter((el) => !value.includes(el));
			const updatedIsDisabledInRestaurants = state.isDisabledInRestaurants.filter((el) => !value.includes(el));
			const updatedIsRestrictedInRestaurants = state.isRestrictedInRestaurants.filter(
				(el) => !value.includes(el),
			);
			const updatedIsPreselectedInRestaurants = state.isPreselectedInRestaurants.filter(
				(el) => !value.includes(el),
			);
			const updatedQuantityRegulationRestaurants = state.quantityRegulationRestaurants.filter(
				(el) => !value.includes(el),
			);
			dispatch({
				type: CreateModifierActionTypes.REMOVE_PRICE,
				payload: {
					updatedTotalPrices,
					updatedRestaurantsForPrices,
					updatedIsDisabledInRestaurants,
					updatedIsRestrictedInRestaurants,
					updatedIsPreselectedInRestaurants,
					updatedQuantityRegulationRestaurants,
				},
			});
		},
		[
			state.totalPrices,
			state.restaurantsForPrice,
			state.isDisabledInRestaurants,
			state.isRestrictedInRestaurants,
			state.isPreselectedInRestaurants,
			state.quantityRegulationRestaurants,
		],
	);

	const setFetchedModifierGroups = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateModifierActionTypes.SET_FETCHED_MODIFIER_GROUPS,
			payload: {
				modifierGroups: value,
			},
		});
	}, []);

	const setModifierGroups = useCallback((value: string[]) => {
		dispatch({
			type: CreateModifierActionTypes.SET_MODIFIER_GROUPS,
			payload: {
				modifierGroups: value,
			},
		});
	}, []);

	const changeName = useCallback((value: string) => {
		dispatch({
			type: CreateModifierActionTypes.CHANGE_NAME,
			payload: {
				name: value,
			},
		});
	}, []);

	const changeDescription = useCallback((value: string) => {
		dispatch({
			type: CreateModifierActionTypes.CHANGE_DESCRIPTION,
			payload: {
				description: value,
			},
		});
	}, []);

	const setDisabledInRestaurants = useCallback((value: string[]) => {
		dispatch({
			type: CreateModifierActionTypes.SET_IS_DISABLED_IN_RESTAURANTS,
			payload: {
				disabledInRestaurants: value,
			},
		});
	}, []);

	const setRestrictedInRestaurants = useCallback((value: string[]) => {
		dispatch({
			type: CreateModifierActionTypes.SET_IS_RESTRICTED_IN_RESTAURANTS,
			payload: {
				restrictedInRestaurants: value,
			},
		});
	}, []);

	const setPreselectedInRestaurants = useCallback((value: string[]) => {
		dispatch({
			type: CreateModifierActionTypes.SET_IS_PRESELECTED_IN_RESTAURANTS,
			payload: {
				preselectedInRestaurants: value,
			},
		});
	}, []);

	const setQuantityRegulationRestaurants = useCallback((value: string[]) => {
		dispatch({
			type: CreateModifierActionTypes.SET_QUANTITY_REGULATION_RESTAURANTS,
			payload: {
				quantityRegulationRestaurants: value,
			},
		});
	}, []);

	const changeQuantityMaxLimit = useCallback((value: number) => {
		dispatch({
			type: CreateModifierActionTypes.CHANGE_QUANTITY_MAX_LIMIT,
			payload: {
				quantityMaxLimit: value,
			},
		});
	}, []);

	const setFetchedTranslations = useCallback(
		(value: TLanguageIDs[], defaultCustomerLanguage: Nullable<IDefaultLanguage>) => {
			const translations = value.map((id) => ({ languageId: id, name: '' }));
			dispatch({
				type: CreateModifierActionTypes.SET_FETCHED_TRANSLATIONS,
				payload: {
					translations,
					defaultCustomerLanguage,
				},
			});
		},
		[],
	);

	const setTranslationTab = useCallback((value: TLanguageIDs | null) => {
		dispatch({
			type: CreateModifierActionTypes.SET_TRANSLATION_TAB,
			payload: {
				selectedTranslationTab: value,
			},
		});
	}, []);

	const changeNameTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							name: value,
					  }
					: translation,
			);
			dispatch({
				type: CreateModifierActionTypes.CHANGE_TRANSLATION,
				payload: {
					translations: updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const changeDescriptionTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							description: value,
					  }
					: translation,
			);
			dispatch({
				type: CreateModifierActionTypes.CHANGE_TRANSLATION,
				payload: {
					translations: updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const changeActivationSchedule = useCallback((value: string) => {
		dispatch({
			type: CreateModifierActionTypes.CHANGE_ACTIVATION_SCHEDULE,
			payload: {
				activationSchedule: value,
			},
		});
	}, []);

	const changeDeactivationSchedule = useCallback((value: string) => {
		dispatch({
			type: CreateModifierActionTypes.CHANGE_DEACTIVATION_SCHEDULE,
			payload: {
				deactivationSchedule: value,
			},
		});
	}, []);

	const setDataForSubmit = useCallback((value: ICreateModifier | null) => {
		dispatch({
			type: CreateModifierActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfCreateModifierModalWindow) => {
		dispatch({
			type: CreateModifierActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const showErrorModalWithInfo = useCallback((value: IServerError<ICreateModifierValidationError> | null) => {
		dispatch({
			type: CreateModifierActionTypes.SHOW_ERROR_MODAL_WITH_INFO,
			payload: {
				errorResponseData: value,
			},
		});
	}, []);

	const resetFields = useCallback(() => {
		dispatch({
			type: CreateModifierActionTypes.RESET_FIELDS,
		});
	}, []);

	return (
		<CreateModifierContext.Provider
			value={{
				changeVisibleForm,
				setFileId,
				setFetchedCustomers,
				setCustomer,
				setFetchedModifierGroups,
				setModifierGroups,
				changeName,
				changeDescription,
				setFetchedRestaurants,
				setRestaurantsForPrice,
				changePrice,
				addPrices,
				removePrice,
				setDisabledInRestaurants,
				setRestrictedInRestaurants,
				setPreselectedInRestaurants,
				setQuantityRegulationRestaurants,
				changeQuantityMaxLimit,
				setFetchedTranslations,
				setTranslationTab,
				changeNameTranslation,
				changeDescriptionTranslation,
				changeActivationSchedule,
				changeDeactivationSchedule,
				setDataForSubmit,
				changeTypeOfModalWindow,
				showErrorModalWithInfo,
				resetFields,
				visibleForm: state.visibleForm,
				fileId: state.fileId,
				fetchedCustomers: state.fetchedCustomers,
				selectedCustomer: state.selectedCustomer,
				fetchedModifierGroups: state.fetchedModifierGroups,
				selectedModifierGroups: state.selectedModifierGroups,
				name: state.name,
				description: state.description,
				fetchedRestaurants: state.fetchedRestaurants,
				restaurantsForPrice: state.restaurantsForPrice,
				price: state.price,
				totalPrices: state.totalPrices,
				isDisabledInRestaurants: state.isDisabledInRestaurants,
				isRestrictedInRestaurants: state.isRestrictedInRestaurants,
				isPreselectedInRestaurants: state.isPreselectedInRestaurants,
				quantityRegulationRestaurants: state.quantityRegulationRestaurants,
				quantityMaxLimit: state.quantityMaxLimit,
				defaultCustomerLanguage: state.defaultCustomerLanguage,
				selectedTranslationTab: state.selectedTranslationTab,
				translations: state.translations,
				activationSchedule: state.activationSchedule,
				deactivationSchedule: state.deactivationSchedule,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
				errorResponseData: state.errorResponseData,
			}}>
			{children}
		</CreateModifierContext.Provider>
	);
};

export default CreateModifierState;
